import { EventEmitter, inject, Injectable } from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '../../environments/environment';
import { partnerValues } from '../constants/template-placeholder-constants';
import { WIDGET_CONSTANTS } from '../constants/widget-constants';
import { CustomFieldsDao } from '../db-models/appointment-custom-fields-dao';
import { CustomBookingMessageTemplate, PartnerDao } from '../db-models/widget-conf-dao';
import { Partner } from '../models/global';
import { LoggerService } from './logger.service';

@Injectable({
  providedIn: 'root'
})
export class HelperService {

  private iconRegistry = inject(MatIconRegistry);
  private sanitizer = inject(DomSanitizer);
  private translate = inject(TranslateService);

  protected readonly widgetConstants = WIDGET_CONSTANTS;
  protected readonly languages = this.widgetConstants.LANGUAGES;
  readonly deployUrl = environment.deployUrl;

  public bookingLoader = new EventEmitter<boolean>();

  /**
   * Rewrites a prefill map's keys from booking-question string identifiers to the
   * matching numeric custom-field id, so the existing id-keyed prefill logic keeps
   * working unchanged. A key that matches a loaded question's `identifier` wins and
   * maps to that question's id; every other key (legacy numeric id, or an unknown
   * identifier) is passed through untouched. Pure + idempotent (case-sensitive exact
   * match); an unknown identifier simply finds no question later and is ignored.
   *
   * @param map prefill map keyed by question id or identifier (value = prefill value)
   * @param customFields the partner's loaded booking questions
   * @returns a new map keyed by numeric id where an identifier matched, else unchanged
   */
  resolveCustomFieldIdentifierKeys(
    map: { [key: string]: any } | undefined,
    customFields: CustomFieldsDao[] | undefined
  ): { [key: string]: any } | undefined {
    if (!map) {
      return map;
    }
    const idByIdentifier = new Map<string, number>();
    for (const cf of customFields ?? []) {
      if (cf?.identifier) {
        idByIdentifier.set(cf.identifier, cf.id);
      }
    }
    const out: { [key: string]: any } = {};
    for (const key of Object.keys(map)) {
      const mapped = idByIdentifier.get(key);
      out[mapped !== undefined ? mapped : key] = map[key];
    }
    return out;
  }

  /**
   * detect IE
   * returns version of IE or false, if browser is not Internet Explorer
   */
  detectIE() {
    const ua = window.navigator.userAgent;

    // Test values; Uncomment to check result …

    // IE 10
    // ua = 'Mozilla/5.0 (compatible; MSIE 10.0; Windows NT 6.2; Trident/6.0)';

    // IE 11
    // ua = 'Mozilla/5.0 (Windows NT 6.3; Trident/7.0; rv:11.0) like Gecko';

    // IE 12 / Spartan
    // ua = 'Mozilla/5.0 (Windows NT 10.0; WOW64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/39.0.2171.71 Safari/537.36 Edge/12.0';

    // Edge (IE 12+)
    // ua = 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36
    // (KHTML, like Gecko) Chrome/46.0.2486.0 Safari/537.36 Edge/13.10586';

    const msie = ua.indexOf('MSIE ');
    if (msie > 0) {
      // IE 10 or older => return version number
      return parseInt(ua.substring(msie + 5, ua.indexOf('.', msie)), 10);
    }

    const trident = ua.indexOf('Trident/');
    if (trident > 0) {
      // IE 11 => return version number
      const rv = ua.indexOf('rv:');
      return parseInt(ua.substring(rv + 3, ua.indexOf('.', rv)), 10);
    }

    const edge = ua.indexOf('Edge/');
    if (edge > 0) {
      // Edge (IE 12+) => return version number
      return parseInt(ua.substring(edge + 5, ua.indexOf('.', edge)), 10);
    }

    // other browser
    return false;
  }

  getDefaultLanguage(partner: Partner | PartnerDao, inputLang: string = null): string {
    const partnerSupportedLang = partner?.supported_widget_languages || [this.widgetConstants.DEFAULT_LANG.split('_')[0]];
    const partnerDefaultLang = partner?.language_identifier || this.widgetConstants.DEFAULT_LANG;
    const browserLang = this.translate.getBrowserCultureLang() || '';
    let languageToBeSet = undefined;

    if (inputLang) {
      for (var i = 0; i < partnerSupportedLang.length; i++) {
        const language = partnerSupportedLang[i];
        if (inputLang.includes(language.toLowerCase())) {
          languageToBeSet = language;
          break;
        }
      }
    }

    if (!languageToBeSet) {
      for (var i = 0; i < partnerSupportedLang.length; i++) {
        const language = partnerSupportedLang[i];
        if (browserLang.includes(language.toLowerCase())) {
          languageToBeSet = language;
          break;
        }
      }
    }

    !languageToBeSet && (languageToBeSet = partnerDefaultLang.split('_')[0]);

    const langObj = this.languages.find(languages => languages.value === languageToBeSet);
    return langObj.locale || partnerDefaultLang;
  }

  sortBy(collection: Array<any>, property: string): Array<any> {
    collection.sort((a: any, b: any) => {
      if ((a[property] ? a[property].toLowerCase() : undefined) < (b[property] ? b[property].toLowerCase() : undefined)) {
        return -1;
      } else if (a[property] > b[property]) {
        return 1;
      } else {
        return 0;
      }
    });
    return collection;
  }

  sortByNumber(collection: Array<any>, property: string): Array<any> {
    collection.sort((a: any, b: any) => {
      if (Number(a[property]) < Number(b[property])) {
        return -1;
      } else if (Number(a[property]) > Number(b[property])) {
        return 1;
      } else {
        return 0;
      }
    });
    return collection;
  }

  getQueryParamValues(urlparams: any, key1: string, key2: string): any {
    if (urlparams[key1]) {
      return urlparams[key1];
    } else if (urlparams[key2]) {
      return urlparams[key2];
    } else {
      return urlparams[key1];
    }
  }

  getQueryStringKey(key: string, query: any, isArray: boolean): any {
    const value: any = this.getQueryStringAsObject(query)[key];
    if (value && isArray) {
      delete value['length'];
      return Object.keys(value).map(function (_) {
        return value[_];
      });
    }
    return value;
  }

  getQueryStringAsObject(query: any) {
    var b, cv, e, k, ma, sk, v, r = {},
      d = function (v) {
        return decodeURIComponent(v).replace(/\+/g, ' ');
      }, // d(ecode) the v(alue)
      q = query, // suggested: q = decodeURIComponent(window.location.search.substring(1)),
      s = /([^&;=]+)=?([^&;]*)/g // original regex that does not allow for ; as a delimiter:   /([^&=]+)=?([^&]*)/g
      ;


    // ma(make array) out of the v(alue)
    ma = function (v) {
      // If the passed v(alue) hasn't been setup as an object
      if (typeof v != 'object') {
        // Grab the cv(current value) then setup the v(alue) as an object
        cv = v;
        v = {};
        v.length = 0;

        // If there was a cv(current value), .push it into the new v(alue)'s array
        //     NOTE: This may or may not be 100% logical to do... but it's better than loosing the original value
        if (cv) {
          Array.prototype.push.call(v, cv);
        }
      }
      return v;
    };

    // While we still have key-value e(ntries) from the q(uerystring) via the s(earch regex)...
    while (e = s.exec(q)) { // while((e = s.exec(q)) !== null) {
      // Collect the open b(racket) location (if any) then set the d(ecoded) v(alue) from the above split key-value e(ntry)
      b = e[1].indexOf('[');
      v = d(e[2]);

      // As long as this is NOT a hash[]-style key-value e(ntry)
      if (b < 0) { // b == "-1"
        // d(ecode) the simple k(ey)
        k = d(e[1]);

        // If the k(ey) already exists
        if (r[k]) {
          // ma(make array) out of the k(ey) then .push the v(alue) into the k(ey)'s array in the r(eturn value)
          r[k] = ma(r[k]);
          Array.prototype.push.call(r[k], v);
        }
        // Else this is a new k(ey), so just add the k(ey)/v(alue) into the r(eturn value)
        else {
          r[k] = v;
        }
      }
      // Else we've got ourselves a hash[]-style key-value e(ntry)
      else {
        // Collect the d(ecoded) k(ey) and the d(ecoded) sk(sub-key) based on the b(racket) locations
        k = d(e[1].slice(0, b));
        sk = d(e[1].slice(b + 1, e[1].indexOf(']', b)));

        // ma(make array) out of the k(ey)
        r[k] = ma(r[k]);

        // If we have a sk(sub-key), plug the v(alue) into it
        if (sk) {
          r[k][sk] = v;
        }
        // Else .push the v(alue) into the k(ey)'s array
        else {
          Array.prototype.push.call(r[k], v);
        }
      }
    }

    // # Return the r(eturn value)
    return r;
  }

  loadPaypalJs(clientId: string) {
    try {
      const script = document.createElement('script');
      script.src = `https://www.paypal.com/sdk/js?client-id=${clientId}`;
      script.defer = true;
      document.head.appendChild(script);
    } catch (ex) {
      LoggerService.error(ex);
    }
  }

  get_random(list: any[]): any {
    return list[Math.floor((Math.random() * list.length))];
  }

  replaceAll(textLabel: string, search: string, replacement: string): string {
    return textLabel.split(search).join(replacement);
  }

  random(max: number, min: number): number {
    return Math.floor(Math.random() * (max - min + 1)) + min;
  }

  /**
   * To sort an array by specified field.
   * Usage: [].sort(sortArrayObjectsByField('sort_by_field_name))
   * @param property Sort field
   * @author Pratik Padia
   */
  sortArrayObjectsByField(property: string) {
    let sortOrder = 1;
    if (property[0] === '-') {
      sortOrder = -1;
      property = property.substring(1);
    }
    return (a: any, b: any) => {
      const valueA = typeof a[property] === 'string' ? a[property].toLowerCase() : a[property];
      const valueb = typeof b[property] === 'string' ? b[property].toLowerCase() : b[property];
      const result = (valueA < valueb) ? -1 : (valueA > valueb) ? 1 : 0;
      return result * sortOrder;
    };
  }

  /**
  * Shuffles an array using Fisher-Yates algorithm
  * @param array Array to shuffle
  * @returns Shuffled array
  */
  shuffleArray<T>(array: T[]): T[] {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  }


  /**
   * To find a template for the templates array
   * @param templates Templates array in which search needs to perform
   * @param identifier Template identifier need to be find from templates array
   * @returns Return template which mathces with identifier
   */
  findTemplate(widgetTemplates: CustomBookingMessageTemplate[], identifier: string): CustomBookingMessageTemplate | null {
    if (widgetTemplates?.length) {
      const template = widgetTemplates?.find(template => template.identifier === identifier);
      if (template) {
        template.is_multi_language = 1;
        return template;
      }
    }

    return null;
  }

  partnerPlaceholders(partner: Partner | undefined): { key: string, value: string }[] {
    return [
      { key: partnerValues.PARTNER_NAME, value: partner?.name?.trim() ?? '' },
      { key: partnerValues.PARTNER_PHONE, value: partner?.phone ?? '' },
      { key: partnerValues.PARTNER_TIMEZONE, value: partner?.timezone ?? '' },
    ];
  }

  /**
   * Validate string is empty or not
   * @param value String value to check if it is empty or not
   * @returns Returns boolean weather string is emprt or not
   */
  isEmptyString(value: string): boolean {
    if (value === 'NULL' || value === 'null' || value === 'undefined' || value === 'UNDEFINED') {
      return true;
    }
    return false;
  }

  /**
   * Identify specific storage is available or not
   * @param type Type of storage to check if it is available or not
   * @returns boolean
   */
  storageAvailable(type: string): boolean {
    try {
      const storage = window?.[type];
      const x = '__calenso_storage_test__';

      storage.setItem(x, x);
      storage.removeItem(x);

      return true;
    } catch (e) {
      return false;
    }
  }

  redirectToUrl(url: string): void {
    var link = document.createElement('a');
    link.href = url;
    link.target = '_blank';
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  /**
   * This function will replace space with + sign in email
   * @param email string
   * @returns string
   */
  fixEmailSpaceIssue(email: string): string {
    return email.replace(/ /g, "+");
  }

  /**
   * Registers a list of SVG icons with the Angular Material icon registry.
   * Each icon is prefixed with 'calio-' followed by its name and '-icon'.
   * The paths are sanitized to ensure they are safe to use in the application.
   *
   * @param svgIcons - An array of objects containing the name and optional path of the SVG icons to be registered.
   *                  Each object should have the following structure:
   *                  { name: string; path?: string; }
   */
  registerSvgIconsList(svgIcons: { name: string; path?: string; }[]): void {
    svgIcons.forEach((svgIcon) => this.iconRegistry.addSvgIcon(
      `calio-${svgIcon.name}-icon`,
      this.sanitizer.bypassSecurityTrustResourceUrl(`${this.deployUrl}/${svgIcon?.path}`)
    ));
  }

  /**
   * Masks a mobile number by revealing the last 4 digits and masking the rest with asterisks.
   * If the number has a country code, it is preserved.
   * @param mobile The mobile number to mask.
   * @returns The masked mobile number.
   */
  maskMobile(mobile: string): string {
    if (!mobile) {
      return mobile;
    }

    if (mobile.startsWith('+')) {
      const countryCodeMatch = mobile.match(/^\+\d{1,4}/);
      const countryCode = countryCodeMatch ? countryCodeMatch[0] : '+';
      const digitsOnly = mobile.replace(countryCode, '').replace(/\D/g, '');

      if (digitsOnly.length <= 2) {
        return mobile;
      }

      const visibleDigits = Math.min(4, Math.ceil(digitsOnly.length * 0.3));
      const maskedLength = digitsOnly.length - visibleDigits;

      const maskedNumber =
        '*'.repeat(maskedLength > 0 ? maskedLength : 0) +
        digitsOnly.substring(maskedLength);

      return `${countryCode}${maskedNumber}`;
    }

    const digitsOnly = mobile.replace(/\D/g, '');

    if (digitsOnly.length <= 2) {
      return mobile;
    }

    const visibleDigits = Math.min(4, Math.ceil(digitsOnly.length * 0.3));
    const maskedLength = digitsOnly.length - visibleDigits;

    return '*'.repeat(maskedLength > 0 ? maskedLength : 0) +
      digitsOnly.substring(maskedLength);
  }

  /**
   * Masks an email address by revealing the first 30% of the local part and masking the rest with asterisks.
   * If the local part is only 1 character, it is shown as is.
   */
  maskEmail(email: string): string {
    if (!email || !email.includes('@')) {
      return email;
    }

    const [localPart, domain] = email.split('@');
    if (localPart.length === 1) {
      return `${localPart}@${domain}`;
    }
    const visibleCount = Math.ceil(localPart.length * 0.3);
    const maskedLocalPart = localPart.substring(0, visibleCount) + '*'.repeat(localPart.length - visibleCount);
    return `${maskedLocalPart}@${domain}`;
  }

  loadIframeResizerJs(version: string) {
    const script = document.createElement('script');
    script.src = version === '5.5.0'
      ? 'https://cdn.jsdelivr.net/npm/@iframe-resizer/child@5.5'
      : 'https://cdnjs.cloudflare.com/ajax/libs/iframe-resizer/4.3.5/iframeResizer.contentWindow.min.js';
    script.defer = true;
    document.head.appendChild(script);
  }
}
